import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Save, Upload } from '@mui/icons-material';
import axios from 'axios';

interface SiteSettings {
  site_title: string;
  site_description: string;
  logo_url: string | null;
  timezone: string;
  default_language: string;
  primary_color: string;
  secondary_color: string;
  contact_email: string | null;
  contact_phone: string | null;
  enable_registration: boolean;
  enable_line_login: boolean;
  enable_email_login: boolean;
  compliance_mode: string;
  enable_activity_logs: boolean;
  enable_consent_management: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function SiteSettingsPage() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/settings/site`);
      setSettings(response.data);
    } catch (error) {
      showSnackbar('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/v1/settings/site`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar('Settings saved successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    const formData = new FormData();
    formData.append('file', logoFile);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v1/settings/site/logo`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          }
        }
      );
      
      setSettings(prev => prev ? { ...prev, logo_url: response.data.logo_url } : null);
      setLogoFile(null);
      showSnackbar('Logo uploaded successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to upload logo', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) return null;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Site Settings
      </Typography>

      <Card>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="General" />
          <Tab label="Branding" />
          <Tab label="Features" />
          <Tab label="Compliance" />
        </Tabs>

        {/* General Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Site Title"
                value={settings.site_title}
                onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Site Description"
                value={settings.site_description || ''}
                onChange={(e) => setSettings({ ...settings, site_description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={settings.timezone}
                  onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                  label="Timezone"
                >
                  <MenuItem value="Asia/Bangkok">Asia/Bangkok (GMT+7)</MenuItem>
                  <MenuItem value="Asia/Singapore">Asia/Singapore (GMT+8)</MenuItem>
                  <MenuItem value="UTC">UTC (GMT+0)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Default Language</InputLabel>
                <Select
                  value={settings.default_language}
                  onChange={(e) => setSettings({ ...settings, default_language: e.target.value })}
                  label="Default Language"
                >
                  <MenuItem value="th">ไทย (Thai)</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="Contact Email"
                value={settings.contact_email || ''}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={settings.contact_phone || ''}
                onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Branding Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Site Logo</Typography>
              {settings.logo_url && (
                <Box mb={2}>
                  <img 
                    src={
                      settings.logo_url.startsWith('http') 
                        ? settings.logo_url 
                        : `https://cdn-aims.amy.care${settings.logo_url.replace('/cdn', '')}`
                    }
                    alt="Site Logo" 
                    style={{ maxHeight: 100, marginBottom: 16 }}
                  />
                </Box>
              )}
              <Box display="flex" gap={2} alignItems="center">
                <Button variant="contained" component="label" startIcon={<Upload />}>
                  Choose Logo
                  <input 
                    type="file" 
                    hidden 
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  />
                </Button>
                {logoFile && (
                  <>
                    <Typography>{logoFile.name}</Typography>
                    <Button variant="outlined" onClick={handleLogoUpload}>
                      Upload
                    </Button>
                  </>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="color"
                label="Primary Color"
                value={settings.primary_color}
                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="color"
                label="Secondary Color"
                value={settings.secondary_color}
                onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Features Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enable_registration}
                    onChange={(e) => setSettings({ ...settings, enable_registration: e.target.checked })}
                  />
                }
                label="Enable User Registration"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enable_line_login}
                    onChange={(e) => setSettings({ ...settings, enable_line_login: e.target.checked })}
                  />
                }
                label="Enable LINE Login"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enable_email_login}
                    onChange={(e) => setSettings({ ...settings, enable_email_login: e.target.checked })}
                  />
                }
                label="Enable Email/Password Login"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Compliance Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Compliance Mode</InputLabel>
                <Select
                  value={settings.compliance_mode}
                  onChange={(e) => setSettings({ ...settings, compliance_mode: e.target.value })}
                  label="Compliance Mode"
                >
                  <MenuItem value="PDPA">PDPA (Thailand)</MenuItem>
                  <MenuItem value="HIPAA">HIPAA (Healthcare US)</MenuItem>
                  <MenuItem value="GDPR">GDPR (Europe)</MenuItem>
                  <MenuItem value="ALL">All Compliance Standards</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enable_activity_logs}
                    onChange={(e) => setSettings({ ...settings, enable_activity_logs: e.target.checked })}
                  />
                }
                label="Enable Activity Logging"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Records all user activities for compliance and security auditing
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enable_consent_management}
                    onChange={(e) => setSettings({ ...settings, enable_consent_management: e.target.checked })}
                  />
                }
                label="Enable Consent Management"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Requires user consent for data collection and processing
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <Divider />

        <Box p={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SiteSettingsPage;
